import React, { useMemo } from 'react';
import styled from 'styled-components';
import { Typography, theme } from 'antd';
import {
  CloseOutlined,
  InfoCircleOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  WarningOutlined,
  NotificationOutlined,
  ThunderboltOutlined,
  StarOutlined,
  SoundOutlined
} from '@ant-design/icons';

const { Text, Link: AntLink } = Typography;

export type AnnouncementAppearance = {
  variant?: 'announcement' | 'update' | 'important';
  showIcon?: boolean;
  iconName?: 'info' | 'success' | 'warning' | 'error' | 'notification' | 'bolt' | 'star' | 'bullhorn';
  closable?: boolean;
  align?: 'left' | 'center';
  bgColor?: string | null;
  borderColor?: string | null;
  textColor?: string | null;
};

export type AnnouncementBannerData = {
  id: string;
  message: string;
  type: 'info' | 'success' | 'warning' | 'error';
  title?: string | null;
  linkText?: string | null;
  linkUrl?: string | null;
  body?: React.ReactNode;
  appearance?: AnnouncementAppearance | null;
};

type Props = {
  data: AnnouncementBannerData;
  onClose?: () => void;
  className?: string;
};

type BannerVisuals = {
  background: string;
  borderColor: string;
  accentColor: string;
  textColor: string;
  align: 'left' | 'center';
  showIcon: boolean;
  closable: boolean;
  iconName: NonNullable<AnnouncementAppearance['iconName']> | undefined;
};

const BannerShell = styled.div<{ $background: string; $borderColor: string; $accentColor: string; $textColor: string }>`
  position: relative;
  width: 100%;
  background: ${({ $background }) => $background};
  border-top: 3px solid ${({ $accentColor }) => $accentColor};
  border-bottom: 1px solid ${({ $borderColor }) => $borderColor};
  color: ${({ $textColor }) => $textColor};
`;

const BannerBody = styled.div<{ align: 'left' | 'center' }>`
  position: relative;
  max-width: 1280px;
  margin: 0 auto;
  min-height: 38px;
  padding: 6px 52px 6px 22px;
  display: flex;
  gap: 9px;
  align-items: center;
  justify-content: ${({ align }) => (align === 'center' ? 'center' : 'flex-start')};
  text-align: ${({ align }) => align};

  @media (max-width: 768px) {
    min-height: auto;
    padding: 10px 46px 10px 16px;
    align-items: flex-start;
    justify-content: flex-start;
    text-align: ${({ align }) => (align === 'center' ? 'center' : 'left')};
  }
`;

const IconWrap = styled.div`
  width: 18px;
  height: 18px;
  flex: 0 0 18px;
  border-radius: 999px;
  background: rgba(148, 163, 184, 0.16);
  box-shadow: inset 0 0 0 1px rgba(148, 163, 184, 0.22);
  font-size: 10px;
  line-height: 1;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ContentWrap = styled.div<{ align: 'left' | 'center' }>`
  display: flex;
  align-items: center;
  justify-content: ${({ align }) => (align === 'center' ? 'center' : 'flex-start')};
  gap: 9px;
  max-width: 100%;
  min-width: 0;
  flex: 1 1 auto;
  flex-wrap: nowrap;

  @media (max-width: 768px) {
    align-items: ${({ align }) => (align === 'center' ? 'center' : 'flex-start')};
    flex-wrap: wrap;
    gap: 5px 8px;
  }
`;

const TitleText = styled(Text)`
  flex: 0 0 auto;
  font-size: 12px;
  font-weight: 700;
  line-height: 1.3;

  &::after {
    content: '';
    display: inline-block;
    width: 1px;
    height: 11px;
    margin-left: 9px;
    vertical-align: -1px;
    background: currentColor;
    opacity: 0.28;
  }

  @media (max-width: 768px) {
    width: 100%;

    &::after {
      display: none;
    }
  }
`;

const MessageText = styled(Text)`
  display: block;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 12px;
  line-height: 1.35;

  @media (max-width: 768px) {
    white-space: normal;
  }
`;

const BodyContent = styled.div<{ align: 'left' | 'center' }>`
  min-width: 0;
  display: inline-flex;
  align-items: center;
  justify-content: ${({ align }) => (align === 'center' ? 'center' : 'flex-start')};
  gap: 9px;
  flex: 0 1 auto;
  overflow: hidden;
  white-space: nowrap;
  font-size: 12px;
  line-height: 1.35;

  a {
    color: inherit;
    font-weight: 700;
    text-decoration: underline;
    text-underline-offset: 3px;
  }

  > div {
    min-width: 0;
    display: inline-flex;
    align-items: center;
    gap: 9px;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }

  > div > div {
    display: inline;
    margin-top: 0 !important;
  }

  br {
    display: none;
  }

  @media (max-width: 768px) {
    display: flex;
    width: 100%;
    flex-wrap: wrap;
    white-space: normal;
    gap: 5px 8px;

    > div {
      display: flex;
      flex-wrap: wrap;
      white-space: normal;
      gap: 5px 8px;
    }
  }
`;

const CtaLink = styled(AntLink)<{ $color: string }>`
  flex: 0 0 auto;
  font-size: 12px;
  font-weight: 700;
  color: ${({ $color }) => $color} !important;
  display: inline-flex;
  align-items: center;
  line-height: 1.2;
  text-decoration: underline;
  text-underline-offset: 3px;
  &:hover {
    color: ${({ $color }) => $color} !important;
    opacity: 0.82;
  }
`;

const CloseButton = styled.button<{ $textColor: string }>`
  position: absolute;
  top: 50%;
  right: 16px;
  transform: translateY(-50%);
  border: none;
  background: transparent;
  color: ${({ $textColor }) => $textColor};
  cursor: pointer;
  width: 24px;
  height: 24px;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  border-radius: 999px;
  transition: background 0.2s ease, opacity 0.2s ease;
  opacity: 0.78;

  &:hover {
    background: rgba(148, 163, 184, 0.16);
    opacity: 1;
  }

  &:focus-visible {
    outline: 2px solid currentColor;
    outline-offset: 2px;
  }
`;

const iconMap: Record<NonNullable<AnnouncementAppearance['iconName']>, React.ReactNode> = {
  info: <InfoCircleOutlined />,
  success: <CheckCircleOutlined />,
  warning: <ExclamationCircleOutlined />,
  error: <WarningOutlined />,
  notification: <NotificationOutlined />,
  bolt: <ThunderboltOutlined />,
  star: <StarOutlined />,
  bullhorn: <SoundOutlined />,
};

function resolveVisuals(
  appearance: AnnouncementAppearance | null | undefined,
  type: AnnouncementBannerData['type'],
  token: ReturnType<typeof theme.useToken>['token']
): BannerVisuals {
  const variant = appearance?.variant || 'announcement';
  const defaults = {
    announcement: {
      background: token.colorBgContainer,
      borderColor: token.colorBorderSecondary,
      accentColor: token.colorInfo,
      textColor: token.colorText,
    },
    update: {
      background: token.colorBgContainer,
      borderColor: token.colorBorderSecondary,
      accentColor: token.colorSuccess,
      textColor: token.colorText,
    },
    important: {
      background: token.colorBgContainer,
      borderColor: token.colorBorderSecondary,
      accentColor: token.colorWarning,
      textColor: token.colorText,
    },
  } as const;

  const base = defaults[variant] || defaults.announcement;

  const background = base.background;
  const borderColor = appearance?.borderColor || base.borderColor;
  const accentColor = appearance?.bgColor || appearance?.borderColor || base.accentColor;
  const textColor = appearance?.textColor || base.textColor || token.colorText;

  const showIcon = appearance?.showIcon !== false;
  const closable = appearance?.closable !== false;

  let iconName = appearance?.iconName;
  if (!iconName) {
    if (type === 'success') iconName = 'success';
    else if (type === 'warning') iconName = 'warning';
    else if (type === 'error') iconName = 'error';
    else iconName = 'info';
  }

  const align = appearance?.align === 'left' ? 'left' : 'center';

  return {
    background,
    borderColor,
    accentColor,
    textColor,
    align,
    showIcon,
    closable,
    iconName,
  };
}

const AnnouncementBanner: React.FC<Props> = ({ data, onClose, className }) => {
  const { token } = theme.useToken();

  const visuals = useMemo(() => resolveVisuals(data.appearance, data.type, token), [data, token]);

  const icon = visuals.iconName ? iconMap[visuals.iconName] : null;

  const linkText = data.linkText?.trim() || 'Learn more';

  const body = data.body ?? (
    <>
      <MessageText style={{ color: visuals.textColor }}>
        {data.message}
      </MessageText>
      {data.linkUrl ? (
        <CtaLink
          $color={visuals.textColor}
          href={data.linkUrl}
          target="_blank"
          rel="noopener noreferrer"
        >
          {linkText}
        </CtaLink>
      ) : null}
    </>
  );

  return (
    <BannerShell
      className={className}
      $background={visuals.background}
      $borderColor={visuals.borderColor}
      $accentColor={visuals.accentColor}
      $textColor={visuals.textColor}
    >
      <BannerBody align={visuals.align}>
        {visuals.showIcon && icon ? (
          <IconWrap aria-hidden>{icon}</IconWrap>
        ) : null}

        <ContentWrap align={visuals.align}>
          <TitleText style={{ color: visuals.textColor }}>
            {data.title?.trim() || 'Announcement'}
          </TitleText>
          <BodyContent align={visuals.align} style={{ color: visuals.textColor }}>
            {body}
          </BodyContent>
        </ContentWrap>

        {visuals.closable && onClose ? (
          <CloseButton
            $textColor={visuals.textColor}
            onClick={onClose}
            aria-label="Dismiss announcement"
          >
            <CloseOutlined />
          </CloseButton>
        ) : null}
      </BannerBody>
    </BannerShell>
  );
};

export default AnnouncementBanner;
export { iconMap, resolveVisuals };
